/**
 * Servicio de peajes Chile — lectura/escritura en Firestore.
 */

import {
  collection, query, where, orderBy, getDocs,
  writeBatch, doc, deleteDoc, getCountFromServer,
} from 'firebase/firestore';
import { db } from '../firebase';
import { generateTollDocId, groupTollRatesByPlaza } from '../utils/tollHelpers';

const COLLECTION = 'peajes_chile';
const BATCH_SIZE = 450;

// ─── Lectura ──────────────────────────────────────────────────────────────────

export async function fetchTollsChile({ year = 2026, activeOnly = true } = {}) {
  const constraints = [where('anio', '==', year)];
  if (activeOnly) constraints.push(where('activo', '==', true));

  try {
    const q = query(
      collection(db, COLLECTION),
      ...constraints,
      orderBy('ordenNorteSur', 'asc')
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    // Fallback without orderBy if index doesn't exist yet
    if (err.code === 'failed-precondition') {
      const q2 = query(collection(db, COLLECTION), ...constraints);
      const snap = await getDocs(q2);
      const records = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      records.sort((a, b) => (a.ordenNorteSur || 99) - (b.ordenNorteSur || 99));
      return records;
    }
    throw err;
  }
}

export function groupTollsByRoute(records = []) {
  const routes = new Map();
  records.forEach((r) => {
    const ruta = r.ruta || 'Sin ruta';
    if (!routes.has(ruta)) routes.set(ruta, []);
    routes.get(ruta).push(r);
  });
  return Object.fromEntries(routes);
}

export function getTollRatesByPlaza(records = []) {
  return groupTollRatesByPlaza(records);
}

export function filterTollsForRoute(records = [], routeNames = []) {
  if (!routeNames.length) return records;
  return records.filter((r) =>
    routeNames.some((name) =>
      String(r.ruta || '').toLowerCase().includes(name.toLowerCase())
    )
  );
}

// ─── Escritura (admin) ────────────────────────────────────────────────────────

export async function countExistingTolls(year = 2026) {
  try {
    const q = query(collection(db, COLLECTION), where('anio', '==', year));
    const snap = await getCountFromServer(q);
    return snap.data().count;
  } catch {
    return 0;
  }
}

export async function deleteExistingTollsByYear(year = 2026, onProgress) {
  const q = query(collection(db, COLLECTION), where('anio', '==', year));
  const snap = await getDocs(q);
  const docs = snap.docs;
  let deleted = 0;

  for (let i = 0; i < docs.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    docs.slice(i, i + BATCH_SIZE).forEach((d) => batch.delete(d.ref));
    await batch.commit();
    deleted += Math.min(BATCH_SIZE, docs.length - i);
    onProgress?.({ deleted, total: docs.length });
  }

  return deleted;
}

export async function uploadTollRecords(records = [], { replaceExisting = false, year = 2026, onProgress } = {}) {
  let deleted = 0;

  if (replaceExisting) {
    deleted = await deleteExistingTollsByYear(year, (p) =>
      onProgress?.({ phase: 'deleting', ...p })
    );
  }

  let written = 0;
  const errors = [];

  for (let i = 0; i < records.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    const chunk = records.slice(i, i + BATCH_SIZE);

    chunk.forEach((record) => {
      const docId = generateTollDocId(record);
      const ref = doc(db, COLLECTION, docId);
      batch.set(ref, {
        ...record,
        creadoEn: new Date().toISOString(),
        actualizadoEn: new Date().toISOString(),
      });
    });

    try {
      await batch.commit();
      written += chunk.length;
      onProgress?.({ phase: 'writing', written, total: records.length });
    } catch (err) {
      errors.push({ batchIndex: i, error: err.message });
    }
  }

  return { ok: errors.length === 0, deleted, written, errors, anio: year };
}
